/* ============================================================================
 * Raisy /pitchdeck — the new React + framer-motion raise deck.
 * "PROPER ENGINE. DESIGN AND ANIMATION AND INTERACTION FIRST."
 * Gate → fast boot → 20 cinematic slides. /pitch (HTML deck) is untouched.
 * ========================================================================== */
import { AnimatePresence, motion } from 'framer-motion';
import { FormEvent, ReactElement, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useSEO } from '@/hooks/useSEO';
import { DECK_SLIDES, PRELOAD, S16Ask, S17Impressum, S18CompanyOS } from './slides';
import { DECK_SLIDES_V2, PRELOAD_V2, S16AskV2 } from './slidesV2';
import { DECK_SLIDES_VISION, PRELOAD_VISION } from './slidesVision';
import { DECK_SLIDES_STORY, PRELOAD_STORY } from './slidesStory';
import {
  APPENDIX_MASTER,
  DECK_SLIDES_MASTER,
  M15Round,
  M16Vision,
  MAppendixDivider,
  PRELOAD_MASTER,
} from './slidesMaster';
import { type DeckMode, useSpeech, wordsWithOffsets } from './deckSpeech';
import { SCRIPT_MASTER } from './speakerScript';
import { type Lead as LeadShape, postLead } from './deckLead';
import './pitchdeck.css';

const BOOT_LINES = ['> Initializing Raisy…', '> loading kernels … ok', '> Raisy ready.'];

/**
 * DECK VERSION TRACKING — the deck's own scheme (`v2026.08` shipped, the
 * co-founder's `v2026.09` is the current PDF, `v2026.09.1` is the challenged
 * build). One entry per version; nothing is ever overwritten, and `/pitchdeck`
 * renders LATEST. The stamp stays printed in the deck itself (title + close).
 *
 * To add a version: append an entry + register its slides. To promote one:
 * set LATEST_DECK_VERSION. Aliases keep old URLs alive (/pitchdeck/v1 = X,
 * /pitchdeck/v2 = X+1).
 */
export type DeckVersion = 'v2026.09' | 'v2026.09.1' | 'v2026.09.2' | 'v2026.09.3' | 'v2026.10.05';
export const LATEST_DECK_VERSION: DeckVersion = 'v2026.10.05';

/* The shape and the endpoint live in ./deckLead, because the appendix captures a lead too. */
export type Lead = LeadShape;

type VersionConfig = {
  label: string;
  stamp: string;
  date: string;
  source: string;
  path: string;
  aliases: string[];
  slides: Array<(p: { dir: number }) => ReactElement>;
  preload: string[];
  /** Read-along SPEAKER SCRIPT, one line per slide in `slides` order plus Ask/Close/Impressum. Rendered as a subtitle strip; omit for decks that have none. (The field keeps its `notes` name — see `speakerScript.ts` for the boundary; the WORDS moved because a reader reads them.) */
  notes?: string[];
  Ask: (p: {
    dir: number;
    nameRef: RefObject<HTMLInputElement | null>;
    lead: Lead;
    /** Opens the appendix from the ask slide — the low-commitment step beside "Schedule a meeting". */
    onOpenAppendix?: () => void;
  }) => ReactElement;
  /**
   * Slides that answer the questions an investor asks WITHOUT us in the room, deliberately not
   * in the main line (D-106). Rendered after the legal slide, counted separately ("Appendix 1/3"),
   * excluded from TOTAL, and carried into the PDF — a REVIEW deck is read cold, so this depth has
   * to live somewhere, and the main line is the one place it must not.
   */
  appendix?: Array<(p: { dir: number }) => ReactElement>;
  /**
   * THE DOOR INTO THE APPENDIX (PO 2026-10-09). Its own field rather than `appendix[0]`, because
   * it is not a page: it carries no number, no note and no content, and a reader who never opens
   * the appendix should still have been told where the pitch ended. Folding it into the array
   * would have made "A1" point at a title card.
   */
  appendixDivider?: (p: { dir: number }) => ReactElement;
  Close: (p: { dir: number }) => ReactElement;
  Impressum: (p: { dir: number }) => ReactElement;
};

export const DECK_VERSIONS: Record<DeckVersion, VersionConfig> = {
  'v2026.09': {
    label: 'X · PDF integration',
    stamp: 'v2026.09',
    date: '2026-09-13',
    source: 'Raisy-The-AI-workforce-that-runs-companies20260913.pdf',
    path: '/pitchdeck/v2026.09',
    aliases: ['v1', 'x', 'v2026-09'],
    slides: DECK_SLIDES,
    preload: PRELOAD,
    Ask: S16Ask,
    Close: S18CompanyOS,
    Impressum: S17Impressum,
  },
  'v2026.09.1': {
    label: 'X+1 · challenged',
    stamp: 'v2026.09.1',
    date: '2026-09-13',
    source: 'per-slide challenge (RULES_PITCH_DECK + RULES_MEAT + RULES_DECISIONS)',
    path: '/pitchdeck/v2026.09.1',
    aliases: ['v2', 'x1', 'v2026-09-1'],
    slides: DECK_SLIDES_V2,
    preload: PRELOAD_V2,
    Ask: S16AskV2,
    Close: S18CompanyOS,
    Impressum: S17Impressum,
  },
  'v2026.09.2': {
    label: 'X+2 · big vision',
    stamp: 'v2026.09.2',
    date: '2026-09-13',
    source: 'vision expansion — PO notes 2026-09-13 (communication standard, signatures, gatekeepers)',
    // Its own top-level URL by request; the alias keeps /pitchdeck/:deckId working too.
    path: '/pitchdeck-big-vision',
    aliases: ['v3', 'x2', 'big-vision', 'v2026-09-2', 'vision'],
    slides: DECK_SLIDES_VISION,
    preload: PRELOAD_VISION,
    Ask: S16AskV2,
    Close: S18CompanyOS,
    Impressum: S17Impressum,
  },
  'v2026.09.3': {
    label: 'X+3 · the story',
    stamp: 'v2026.09.3',
    date: '2026-09-13',
    source: 'story build — hammer + the All-Rounder Formula (economy_harness research)',
    path: '/pitchdeck-story',
    aliases: ['v4', 'x3', 'story', 'v2026-09-3'],
    slides: DECK_SLIDES_STORY,
    preload: PRELOAD_STORY,
    Ask: S16AskV2,
    Close: S18CompanyOS,
    Impressum: S17Impressum,
  },
  'v2026.10.05': {
    label: 'MASTER · the cherry-pick',
    stamp: 'v2026.10.05',
    date: '2026-10-05',
    source: 'the four-deck comparison (@P109 D-072) — mostly the co-founder\'s v10, with X+1\'s structure and ask and the PDF\'s competition slide',
    path: '/pitchdeck/v2026.10.05',
    aliases: ['v5', 'x4', 'master', 'v2026-10-05', 'v2026.10'],
    slides: DECK_SLIDES_MASTER,
    preload: PRELOAD_MASTER,
    notes: SCRIPT_MASTER,
    appendix: APPENDIX_MASTER,
    appendixDivider: MAppendixDivider,
    Ask: M15Round,
    Close: M16Vision,
    Impressum: S17Impressum,
  },
};

/** Resolve a URL segment (`v2026.09`, `v1`, `x`, …) to a version id. */
export function resolveDeckVersion(segment?: string): DeckVersion | null {
  if (!segment) return null;
  const s = segment.toLowerCase();
  const ids = Object.keys(DECK_VERSIONS) as DeckVersion[];
  const direct = ids.find((id) => id.toLowerCase() === s);
  if (direct) return direct;
  return ids.find((id) => DECK_VERSIONS[id].aliases.includes(s)) ?? null;
}

type Phase = 'gate' | 'boot' | 'deck';

export const PitchDeckV2Page = ({ version = LATEST_DECK_VERSION }: { version?: DeckVersion }) => {
  const V = DECK_VERSIONS[version];
  /*
   * FOUR SEQUENCES, AND THE COUNTER SAYS WHICH ONE YOU ARE IN (PO 2026-10-09: "or 16th slide is
   * the last?"). The old arithmetic numbered everything in one run, so 16 pages were shown as
   * "16 / 16" while the pitch itself ended at 15 — and the legal page read as the finale of the
   * deck. Each run now has its own label:
   *
   *   the pitch      01 / 15 … 13/15, Ask 14/15, Close 15/15   (the legal page is not the pitch)
   *   the legal page "Legal"                                    (unerased, deliberately unnumbered)
   *   the appendix   "Appendix" on the door, then A1 / A4 … A4 / A4
   */
  const PITCH = V.slides.length + 2; // content + Ask + Close — what a reader would call "the deck"
  const TOTAL = V.slides.length + 3; // + the legal page
  const APPENDIX = V.appendix ?? [];
  const DIVIDER = V.appendixDivider;
  const DIVIDER_AT = DIVIDER ? TOTAL : -1;
  const APPENDIX_AT = TOTAL + (DIVIDER ? 1 : 0);
  const TOTAL_ALL = TOTAL + (DIVIDER ? 1 : 0) + APPENDIX.length; // what the nav may walk through
  useSEO({
    title: 'Raisy — Every decision leaves a receipt',
    description:
      'Raisy is the AI workforce platform: paste your URL, get a governed, auditable AI workforce — fundraising, sales, recruiting, support — with approval gates and versioned, self-improving kernels. AI prepares. Humans decide.',
    keywords:
      'AI workforce, AI employees, governed autonomy, AI workforce platform, fundraising AI, sales AI, approval gates, audit trail',
    ogTitle: 'Raisy — Every decision leaves a receipt',
    ogDescription: 'A governed AI workforce with approval gates, audit trails, and self-improving kernels.',
    ogImage: 'https://raisy.team/pitch/artsy/c.jpg',
    ogType: 'website',
    canonicalUrl: `https://raisy.team${V.path}`,
  });

  /* ------------------------------------------------------------- phase + gate */
  const [phase, setPhase] = useState<Phase>(() => {
    // SESSION-SCOPED, not forever (D-107). This used to be localStorage, which meant a visitor
    // who entered ONCE was never shown the gate again on that browser — including the people
    // reviewing the deck, for whom the gate is part of what they are reviewing. The PO's
    // objection was the right one: it "doesn't sound transparent, transparency is usually trust."
    // The gate is a convenience so a reader is not asked twice in one sitting; the RECORD of who
    // arrived is the lead POST below, which goes to the one centralised memory every inbound and
    // outbound channel writes to. A browser flag is not a record.
    try {
      return sessionStorage.getItem('pd_entered') === '1' ? 'deck' : 'gate';
    } catch {
      return 'gate';
    }
  });
  const [gateErr, setGateErr] = useState('');
  const [sending, setSending] = useState(false);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  /* The identity captured at the gate, kept so the ASK slide does not ask for it a
     second time. Asking twice is the tell that the first ask was for us, not for them. */
  const [lead, setLead] = useState<Lead>(() => {
    try {
      const raw = sessionStorage.getItem('pd_lead');
      if (raw) {
        const p = JSON.parse(raw) as Partial<Lead>;
        return { name: p.name || '', email: p.email || '' };
      }
    } catch {
      /* storage unavailable — fall through to an empty lead */
    }
    return { name: '', email: '' };
  });

  const enterGate = () => {
    setPhase('boot');
    // This sitting only — see the note on the phase initialiser (D-107).
    try {
      sessionStorage.setItem('pd_entered', '1');
    } catch {
      /* storage unavailable — non-fatal */
    }
    // warm the deck images while the boot terminal types
    V.preload.forEach((src) => {
      const i = new Image();
      i.src = src;
    });
  };

  const onGateSubmit = (e: FormEvent) => {
    e.preventDefault();
    const name = nameRef.current?.value.trim() || '';
    const email = emailRef.current?.value.trim() || '';
    if (!name || !email) return;
    setSending(true);
    setGateErr('');
    setLead({ name, email });
    try {
      sessionStorage.setItem('pd_lead', JSON.stringify({ name, email }));
    } catch {
      /* storage unavailable — the ask slide falls back to no greeting */
    }
    // Best-effort lead log — the deck must NEVER be blocked by the logging
    // backend. Race the waitlist POST against a short timeout so the user enters
    // immediately even if the backend is slow or unavailable. The POST itself is
    // ./deckLead's, the same one the appendix's request button uses.
    const maxWait = new Promise<void>((resolve) => setTimeout(resolve, 1200));
    void Promise.race([postLead({ name, email }, 'PITCHDECK'), maxWait]).finally(enterGate);
  };

  /* ------------------------------------------------------------------- boot */
  const [bootLine, setBootLine] = useState('');

  useEffect(() => {
    if (phase !== 'boot') return;
    let i = 0;
    let j = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const step = () => {
      const line = BOOT_LINES[i];
      if (j <= line.length) {
        setBootLine(line.slice(0, j++));
        timer = setTimeout(step, 8);
      } else {
        j = 0;
        i += 1;
        if (i < BOOT_LINES.length) {
          setBootLine('');
          timer = setTimeout(step, 60);
        } else {
          timer = setTimeout(() => setPhase('deck'), 260);
        }
      }
    };
    step();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [phase]);

  /* ------------------------------------------------------------------- deck */
  const [idx, setIdx] = useState(0);
  const [direction, setDirection] = useState(1);
  /* `idx` has to exist before this can be derived from it — the reason it is here and not with the
     rest of the arithmetic above. The DIVIDER counts as the appendix: it is the page that tells
     the reader the mode changed, so it must already wear the appendix's colour. */
  const inAppendix = DIVIDER ? idx >= DIVIDER_AT : idx >= APPENDIX_AT;
  const deckRef = useRef<HTMLDivElement | null>(null);
  /*
   * THE MASTER CHOICE (PO 2026-10-09): Reading or Speaking.
   *
   *   READING  — the reader sets the pace; the note is a prompt they read silently. The default,
   *              and the mode the deck is reviewed in.
   *   SPEAKING — the deck sets the pace; the note is spoken and the subtitle tracks the words.
   *
   * They share ONE script: `V.notes`. A second script for the spoken version would be a second
   * home for the same words, and the two would disagree within a week.
   */
  const [mode, setMode] = useState<DeckMode>('reading');
  const [playing, setPlaying] = useState(false);
  /* Read-along notes: on by default (the PO reviews the deck by reading it), hideable so the slide can be seen clean. */
  const [showNotes, setShowNotes] = useState(true);
  const spoken = mode === 'speaking';
  /* In SPEAKING mode the note IS the subtitle, so hiding it would silence the deck's own words. */
  const note = ((showNotes || spoken) && V.notes?.[idx]) || '';
  const {
    supported: speechOk,
    speaking,
    at,
    failed: speechFailed,
    recorded,
    playClip,
    speak,
    stop: stopSpeech,
  } = useSpeech();
  /* Which word the voice is on. `at` is a CHARACTER index (the engine's own boundary report), so
     the mapping to a word index is the last token whose offset it has passed — no re-scan. */
  const words = spoken ? wordsWithOffsets(note) : [];
  const atWord = (() => {
    if (!spoken || at < 0) return -1;
    let w = -1;
    for (let i = 0; i < words.length && words[i]!.i <= at; i++) w = i;
    return w;
  })();

  const go = useCallback(
    (next: number) => {
      setIdx((cur) => {
        const target = Math.max(0, Math.min(TOTAL_ALL - 1, next));
        if (target === cur) return cur;
        setDirection(target > cur ? 1 : -1);
        return target;
      });
    },
    [],
  );

  /*
   * THE PLAY LOOP. One effect, keyed on the slide, so a voice can never outlive the page it was
   * reading: changing slide runs the cleanup first and cancels whatever is mid-sentence, then the
   * new slide's note starts. That also makes manual paging during playback correct for free.
   *
   * A slide with no note ADVANCES rather than stopping — the divider and the legal page are not
   * the end of the story, and a play button that silently dies two pages before the end is worse
   * than one that skips a page with nothing to say.
   */
  useEffect(() => {
    if (!spoken || !playing) return;
    /* The engine said it cannot speak (no voice on this machine). Stop the run rather than
       advancing silently through the deck. */
    if (speechFailed) { setPlaying(false); return; }
    if (idx >= TOTAL_ALL - 1) {
      setPlaying(false);
      return;
    }
    if (!note) {
      go(idx + 1);
      return;
    }
    /* ⚠️ RECORDED FIRST, BROWSER SECOND (PO 2026-10-09: *"the tts is terrible"*). `playClip`
       resolves false when the slide has no recording — a 404, or assets not yet made — and only
       then does the browser engine speak. So shipping fifteen mp3s is the entire improvement, and
       a missing one degrades the voice rather than the run. */
    let cancelled = false;
    void playClip(idx, () => go(idx + 1)).then((played) => {
      if (!cancelled && !played) speak(note, () => go(idx + 1));
    });
    return () => {
      cancelled = true;
      stopSpeech();
    };
    /* `go`/`speak`/`stopSpeech` are stable; `note` is derived from `idx`. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spoken, playing, idx, note, TOTAL_ALL, speechFailed, playClip, speak, go]);

  /* Leaving speaking mode must not leave a voice running. */
  useEffect(() => {
    if (!spoken) { setPlaying(false); stopSpeech(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spoken]);

  /* keyboard */
  useEffect(() => {
    if (phase !== 'deck') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        go(idx + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        go(idx - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        go(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        go(TOTAL - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, idx, go]);

  /* touch swipe */
  useEffect(() => {
    if (phase !== 'deck') return;
    let tx0: number | null = null;
    const onStart = (e: TouchEvent) => {
      tx0 = e.touches[0].clientX;
    };
    const onEnd = (e: TouchEvent) => {
      if (tx0 === null) return;
      const dx = e.changedTouches[0].clientX - tx0;
      if (Math.abs(dx) > 50) go(idx + (dx < 0 ? 1 : -1));
      tx0 = null;
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchend', onEnd);
    };
  }, [phase, idx, go]);

  /* wheel with scrollable-slide bypass + lock */
  const wheelLock = useRef(false);
  useEffect(() => {
    if (phase !== 'deck') return;
    const onWheel = (e: WheelEvent) => {
      if (wheelLock.current) return;
      const dy = e.deltaMode === 1 ? e.deltaY * 40 : e.deltaY;
      if (Math.abs(dy) < 12) return;
      const act = deckRef.current?.querySelector('.pd-slide');
      if (act && act.scrollHeight > act.clientHeight + 2) {
        const down = act.scrollTop + act.clientHeight < act.scrollHeight - 2;
        const up = act.scrollTop > 2;
        if ((dy > 0 && down) || (dy < 0 && up)) return; // let native scroll
      }
      wheelLock.current = true;
      go(idx + (dy > 0 ? 1 : -1));
      setTimeout(() => {
        wheelLock.current = false;
      }, 500);
    };
    window.addEventListener('wheel', onWheel, { passive: true });
    return () => window.removeEventListener('wheel', onWheel);
  }, [phase, idx, go]);

  const printDeck = () => window.print();
  const Current = V.slides[idx];

  return (
    <div className="pd">
      {/* ------------------------------------------------ GATE */}
      <AnimatePresence>
        {phase === 'gate' && (
          <motion.div
            className="pd-gate"
            key="gate"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.96, filter: 'blur(6px)' }}
            transition={{ duration: 0.5 }}
          >
            <motion.form
              className="box"
              onSubmit={onGateSubmit}
              initial={{ opacity: 0, y: 40, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="logo">raisy</div>
              <div className="word">private pitch · {V.stamp} · {TOTAL} slides</div>
              <h2>Get access to the Raisy deck</h2>
              <p>Just a name + email so we know who we're talking to. Everything logs to one sheet.</p>
              <input ref={nameRef} placeholder="Your name" required />
              <input ref={emailRef} type="email" placeholder="you@company.com" required />
              <button type="submit" disabled={sending}>
                {sending ? 'Opening…' : 'Enter the deck'}
              </button>
              <div className="err">{gateErr}</div>
              <div className="foot">raisy.team · every decision leaves a receipt</div>
            </motion.form>
          </motion.div>
        )}

        {/* ------------------------------------------------ BOOT (fast) */}
        {phase === 'boot' && (
          <motion.div
            className="pd-boot"
            key="boot"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, filter: 'blur(6px)' }}
            transition={{ duration: 0.4 }}
          >
            <div className="win">
              <div className="bar">
                <span className="dot r" />
                <span className="dot y" />
                <span className="dot g" />
              </div>
              <div className="term">{bootLine}</div>
            </div>
            <div className="sub">raisy.team · every decision leaves a receipt</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ------------------------------------------------ DECK */}
      {phase === 'deck' && (
        <div className="pd-deck" ref={deckRef}>
          <div className="pd-topbar">
            <span className="logo">
              raisy<b>.team</b>
            </span>
            <span className="pd-top-right">
              {/* THE MASTER CHOICE. Reading = the reader sets the pace. Speaking = the deck does,
                  and the note becomes a spoken subtitle. Two words, one segmented control — the
                  choice is permanent for the session and it changes what the deck IS, so it sits
                  before the incidental buttons rather than among them. */}
              {V.notes && speechOk && (
                <span className="pd-mode" role="group" aria-label="Presentation mode">
                  {(['reading', 'speaking'] as const).map((m) => (
                    <button
                      key={m}
                      className={`pd-mode-b${mode === m ? ' on' : ''}`}
                      onClick={() => setMode(m)}
                      aria-pressed={mode === m}
                      title={
                        m === 'reading'
                          ? 'Reading presentation — you set the pace'
                          : 'Speaking presentation — the deck reads its own notes aloud'
                      }
                    >
                      {m === 'reading' ? 'Reading' : 'Speaking'}
                    </button>
                  ))}
                </span>
              )}
              {spoken && (
                /* DISABLED WHERE THERE IS NOTHING TO READ, and it says which of the two reasons
                   applies — no note on this page, or no voice on this machine. A live control
                   that does nothing is the fault this deck has now reported three times. */
                <button
                  className={`pd-printbtn pd-playbtn${playing ? ' on' : ''}`}
                  onClick={() => setPlaying((v) => !v)}
                  disabled={(!note && !playing) || speechFailed}
                  title={
                    speechFailed
                      ? 'This browser has no voice installed, so the deck cannot read itself aloud'
                      : !note
                        ? 'This page carries no speaker note — nothing to read aloud'
                        : playing
                          ? 'Pause'
                          : 'Play the deck aloud, from this slide'
                  }
                  aria-pressed={playing}
                >
                  {speechFailed ? 'No voice' : playing ? '❚❚ Pause' : '▶ Play'}
                </button>
              )}
              {V.notes && !spoken && (
                /* ⚠️ THE APPENDIX HAS NO NOTES BY CONSTRUCTION, and the button used to stay live
                   on those pages anyway — so it read as a broken button rather than as a page with
                   nothing to say. It is disabled and it says which page it is on. */
                <button
                  className={`pd-printbtn pd-notesbtn${showNotes ? ' on' : ''}`}
                  onClick={() => setShowNotes((s) => !s)}
                  disabled={!V.notes?.[idx]}
                  title={
                    V.notes?.[idx]
                      ? showNotes
                        ? 'Hide the speaker script'
                        : 'Show the speaker script'
                      : idx >= APPENDIX_AT
                        ? 'The appendix is read, not presented — it carries no speaker note'
                        : 'This page carries no speaker note'
                  }
                  aria-pressed={showNotes}
                >
                  Speaker notes
                </button>
              )}
              <button className="pd-printbtn" onClick={printDeck} title="Save as PDF">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9V3h12v6" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
                PDF
              </button>
              <span className={`pd-count${inAppendix ? ' appx' : ''}`}>
                {/* ONE COUNTER, THREE RUNS (see the arithmetic above). The legal page is not the
                    pitch and the appendix is not the deck, so neither borrows the pitch's numbers. */}
                {idx < PITCH
                  ? `${V.stamp} · ${String(idx + 1).padStart(2, '0')} / ${String(PITCH).padStart(2, '0')}`
                  : idx === PITCH
                    ? `${V.stamp} · Legal`
                    : idx === DIVIDER_AT
                      ? `${V.stamp} · Appendix`
                      : `${V.stamp} · Appendix A${idx - APPENDIX_AT + 1} / ${APPENDIX.length}`}
              </span>
            </span>
          </div>
          <div className="pd-progress" style={{ width: `${((idx + 1) / TOTAL_ALL) * 100}%` }} />

          {/* overlapping transition = snappy between slides */}
          <AnimatePresence custom={direction}>
            {Current ? (
              <Current key={idx} dir={direction} />
            ) : idx === V.slides.length ? (
              <V.Ask
                key={idx}
                dir={direction}
                nameRef={nameRef}
                lead={lead}
                /* THE BUTTON LANDS WHERE ITS LABEL SAYS. It reads "appendix A4", so it opens the
                   MODEL page — which is the last appendix entry by registry convention, and the
                   one thing on that slide the reader actually asked for. Opening A1 instead put
                   the reader one page short of the number they clicked on. */
                onOpenAppendix={APPENDIX.length ? () => go(APPENDIX_AT + APPENDIX.length - 1) : undefined}
              />
            ) : idx === V.slides.length + 1 ? (
              <V.Close key={idx} dir={direction} />
            ) : idx === V.slides.length + 2 ? (
              <V.Impressum key={idx} dir={direction} />
            ) : idx === DIVIDER_AT && DIVIDER ? (
              <DIVIDER key={idx} dir={direction} />
            ) : (
              (() => {
                const A = APPENDIX[idx - APPENDIX_AT];
                return A ? <A key={idx} dir={direction} /> : null;
              })()
            )}
          </AnimatePresence>

          {/* watermark — never on the title */}
          {idx !== 0 && (
            <div className="pd-wm">
              <img src="/pitch/raisy_white.png" alt="" />
              Raisy
            </div>
          )}

          {note && (
            <aside className={`pd-notes${spoken ? ' pd-notes-speak' : ''}`} aria-live="polite">
              <span className="pd-notes-tag">
                {spoken ? (speaking ? (recorded ? 'Playing' : 'Speaking') : 'Subtitle') : 'Speaker note'}
              </span>
              {spoken ? (
                /* THE SUBTITLE IS THE SAME SENTENCE, ONE WORD PER SPAN. Nothing is re-written for
                   the spoken version — only split, so the highlighted word is provably the word
                   being said. */
                <span className="pd-notes-words">
                  {words.map((w, i) => (
                    <span key={i} className={i === atWord ? 'on' : undefined}>
                      {w.w}{' '}
                    </span>
                  ))}
                </span>
              ) : (
                note
              )}
            </aside>
          )}

          <nav className="pd-nav">
            <button disabled={idx === 0} onClick={() => go(idx - 1)}>
              ‹ Prev
            </button>
            <button disabled={idx === TOTAL_ALL - 1} onClick={() => go(idx + 1)}>
              Next ›
            </button>
          </nav>
        </div>
      )}

      {/* print deck — hidden on screen, one slide per page in print */}
      <div className="pd-print" aria-hidden>
        {V.slides.map((S, i) => (
          <S key={`p${i}`} dir={1} />
        ))}
        <V.Ask key={`p${V.slides.length}`} dir={1} nameRef={nameRef} lead={lead} onOpenAppendix={undefined} />
        <V.Close key={`p${V.slides.length + 1}`} dir={1} />
        <V.Impressum key={`p${V.slides.length + 2}`} dir={1} />
        {/* The appendix IS printed: it is the part of the deck that answers questions when we are
            not in the room, and the PDF is the artefact that travels without us. The divider is
            printed with it — in a PDF there is no top bar to tell the reader the pitch has ended,
            so the page has to do it. */}
        {DIVIDER && <DIVIDER key="padiv" dir={1} />}
        {APPENDIX.map((A, i) => (
          <A key={`pa${i}`} dir={1} />
        ))}
      </div>
    </div>
  );
};
